import React, { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, Lock } from "@phosphor-icons/react";

export const PAY_METHODS = [
  { v: "bank", label: "Bank Transfer (NEFT/RTGS)" }, { v: "cash", label: "Cash" },
  { v: "cheque", label: "Cheque" }, { v: "upi", label: "UPI" },
];

const Lines = ({ title, rows, setRows, readOnly, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="eyebrow">{title}</div>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <Input className="h-9" placeholder="Name e.g. HRA" value={r.name} disabled={readOnly}
          onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`${testid}-name-${i}`} />
        <Input className="h-9 w-36 font-mono" type="number" min="0" value={r.amount} disabled={readOnly}
          onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} data-testid={`${testid}-amount-${i}`} />
        {!readOnly && <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => setRows(rows.filter((_, j) => j !== i))}><X size={13} /></Button>}
      </div>
    ))}
    {!readOnly && <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => setRows([...rows, { name: "", amount: "" }])} data-testid={`${testid}-add`}><Plus size={12} className="mr-1" />Add</Button>}
  </div>
);

export const SalaryDetails = ({ emp, canEdit, onSaved }) => {
  const s = emp.salary_structure || {};
  const [basic, setBasic] = useState(s.basic ?? emp.monthly_salary ?? 0);
  const [alw, setAlw] = useState(s.allowances || []);
  const [ded, setDed] = useState(s.deductions || []);
  const [method, setMethod] = useState(emp.payment_method || "bank");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const x = emp.salary_structure || {};
    setBasic(x.basic ?? emp.monthly_salary ?? 0); setAlw(x.allowances || []); setDed(x.deductions || []); setMethod(emp.payment_method || "bank");
  }, [emp]);
  const sum = (rows) => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const net = (Number(basic) || 0) + sum(alw) - sum(ded);

  const save = async () => {
    if (net <= 0) { toast.error("Monthly salary must be more than zero"); return; }
    setSaving(true);
    try {
      await api.patch(`/salary/employees/${emp.id}`, {
        salary_structure: { basic: Number(basic) || 0, allowances: alw, deductions: ded }, payment_method: method,
      });
      toast.success("Salary details saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-4 space-y-4" data-testid="hr-salary-section">
      {!canEdit && (
        <div className="text-[12px] text-muted-foreground flex items-center gap-1.5" data-testid="hr-salary-locked">
          <Lock size={13} /> Only Admin or Regional Manager can set or change salary.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className="eyebrow">Basic Salary (monthly) *</Label>
          <Input type="number" min="0" value={basic} disabled={!canEdit} onChange={(e) => setBasic(e.target.value)} className="font-mono" data-testid="hr-salary-basic" /></div>
        <div className="space-y-1.5"><Label className="eyebrow">Payment Method</Label>
          <Select value={method} onValueChange={setMethod} disabled={!canEdit}>
            <SelectTrigger data-testid="hr-salary-method"><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <Lines title="Allowances" rows={alw} setRows={setAlw} readOnly={!canEdit} testid="hr-salary-allowances" />
        <Lines title="Deductions (PF, ESI, PT…)" rows={ded} setRows={setDed} readOnly={!canEdit} testid="hr-salary-deductions" />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 border-t border-border pt-3">
        <div className="text-sm" data-testid="hr-salary-net">Monthly salary used in payroll: <b className="font-mono">{inr(net)}</b>
          <span className="text-[11px] text-muted-foreground ml-2">= Basic + Allowances − Deductions</span></div>
        {canEdit && <Button onClick={save} disabled={saving} data-testid="hr-salary-save">Save Salary Details</Button>}
      </div>
    </div>
  );
};
